import React from 'react';
import { PreviewCardShell } from './PreviewCardShell';
import { renderMockupByType } from './renderMockupByType';
import { AlertCircle, Scale } from 'lucide-react';
import { useYouTubeShortDetection } from '@/hooks/useYouTubeShortDetection';

interface ComparisonItem {
  url?: string;
  type?: string;
  props?: Record<string, string>;
}

interface ComparisonViewMockupProps {
  items: string; // JSON-stringified array of ComparisonItem[]
  previewLabel?: string;
}

// Video platform detection
const getVideoType = (url: string): 'youtube' | 'tiktok' | 'instagram' | null => {
  const urlLower = url.toLowerCase();
  if (urlLower.includes('youtube.com') || urlLower.includes('youtu.be')) {
    return 'youtube';
  }
  if (urlLower.includes('tiktok.com')) {
    return 'tiktok';
  }
  if (urlLower.includes('instagram.com')) {
    return 'instagram';
  }
  return null;
};

// Generate embed URL for videos
const getEmbedUrl = (url: string, type: 'youtube' | 'tiktok' | 'instagram'): string | null => {
  try {
    switch (type) {
      case 'youtube': {
        const urlObj = new URL(url);
        let videoId: string | null = null;
        if (urlObj.hostname.includes('youtube.com')) {
          const shortsMatch = urlObj.pathname.match(/\/shorts\/([^\/\?]+)/);
          if (shortsMatch) {
            videoId = shortsMatch[1];
          } else {
            videoId = urlObj.searchParams.get('v');
          }
        } else if (urlObj.hostname.includes('youtu.be')) {
          videoId = urlObj.pathname.slice(1).split('?')[0];
        }
        return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
      }
      case 'tiktok': {
        const match = url.match(/\/video\/(\d+)/);
        const videoId = match ? match[1] : null;
        return videoId ? `https://www.tiktok.com/embed/v2/${videoId}` : null;
      }
      case 'instagram': {
        const postMatch = url.match(/\/p\/([^\/\?]+)/);
        const reelMatch = url.match(/\/reel\/([^\/\?]+)/);
        const id = postMatch?.[1] || reelMatch?.[1];
        return id ? `https://www.instagram.com/p/${id}/embed/` : null;
      }
      default:
        return null;
    }
  } catch (error) {
    console.error('Error creating embed URL:', error);
    return null;
  }
};

interface VideoEmbedProps {
  url: string;
  index: number;
}

// VideoEmbed is a separate component so useYouTubeShortDetection (a hook)
// can be called per item — hooks cannot be called inside .map().
const VideoEmbed = ({ url, index }: VideoEmbedProps) => {
  const videoType = getVideoType(url);
  const embedUrl = videoType ? getEmbedUrl(url, videoType) : null;
  const aspectRatio = useYouTubeShortDetection(url);

  if (!embedUrl) {
    return (
      <div
        className="flex items-center justify-center bg-zinc-800 rounded-lg text-zinc-400 text-xs p-4"
        style={{ aspectRatio: '9/16' }}
      >
        <div className="text-center">
          <AlertCircle className="h-6 w-6 mx-auto mb-2" />
          <p>Invalid video URL</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative w-full bg-black rounded-lg overflow-hidden"
      style={{ aspectRatio }}
    >
      <iframe
        src={embedUrl}
        className="absolute inset-0 w-full h-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        scrolling="no"
        frameBorder="0"
        title={`Video ${index + 1}`}
      />
    </div>
  );
};

export const ComparisonViewMockup = ({
  items,
  previewLabel = 'Content Comparison',
}: ComparisonViewMockupProps) => {
  const parsedItems = React.useMemo(() => {
    try {
      const parsed = JSON.parse(items) as ComparisonItem[];
      return Array.isArray(parsed) ? parsed.slice(0, 4) : [];
    } catch (error) {
      console.error('Failed to parse comparison items:', error);
      return [];
    }
  }, [items]);

  if (parsedItems.length < 2) {
    return (
      <PreviewCardShell
        badge={<Scale className="h-4 w-4 text-white" />}
        label={previewLabel}
        badgeClassName="bg-gradient-to-r from-indigo-500 to-purple-500"
      >
        <div className="flex items-center gap-2 p-4 text-zinc-400">
          <AlertCircle className="h-4 w-4" />
          <span className="text-sm">Comparison requires at least 2 items</span>
        </div>
      </PreviewCardShell>
    );
  }

  return (
    <PreviewCardShell
      badge={<Scale className="h-4 w-4 text-white" />}
      label={previewLabel}
      badgeClassName="bg-gradient-to-r from-indigo-500 to-purple-500"
    >
      <div className="p-3 grid grid-cols-1 md:grid-cols-2 gap-3">
        {parsedItems.map((item, index) => {
          if (item.url) {
            return <VideoEmbed key={index} url={item.url} index={index} />;
          }

          if (item.type && item.props) {
            return (
              <div
                key={index}
                className="relative transform transition-transform hover:scale-[1.02] md:scale-[0.85] md:origin-top"
              >
                {renderMockupByType(item.type, item.props, index)}
              </div>
            );
          }

          return (
            <div
              key={index}
              className="flex items-center justify-center bg-zinc-800 rounded-lg text-zinc-400 text-xs p-4"
              style={{ aspectRatio: '9/16' }}
            >
              <div className="text-center">
                <AlertCircle className="h-6 w-6 mx-auto mb-2" />
                <p>Invalid item format</p>
              </div>
            </div>
          );
        })}
      </div>
    </PreviewCardShell>
  );
};
